// Statutory employer payroll taxes by year and state (PRD §3.2, EC8). The parameters live in an
// admin-edited table that Tom confirms each January; nothing here hard-codes a rate.

export interface PayrollTaxYear {
  year: number;
  socialSecurity: { rate: number; wageBase: number };
  medicare: { rate: number };
  futa: { rate: number; wageBase: number };
  /** State unemployment, by two-letter state code. */
  suta: Record<string, { rate: number; wageBase: number }>;
}

export interface EmployerTaxes {
  socialSecurity: number;
  medicare: number;
  futa: number;
  suta: number;
  total: number;
}

export class MissingTaxRateError extends Error {
  constructor(year: number, state: string) {
    super(`The ${year} payroll tax table has no unemployment (SUTA) rate for ${state}.`);
    this.name = "MissingTaxRateError";
  }
}

/** Employer taxes on a year's wages, each tax capped at its wage base. */
export function employerTaxes(annualWages: number, table: PayrollTaxYear, state: string): EmployerTaxes {
  const suta = table.suta[state];
  if (!suta) throw new MissingTaxRateError(table.year, state);
  const capped = (base: number) => Math.min(annualWages, base);
  const socialSecurity = capped(table.socialSecurity.wageBase) * table.socialSecurity.rate;
  const medicare = annualWages * table.medicare.rate;
  const futa = capped(table.futa.wageBase) * table.futa.rate;
  const sutaTax = capped(suta.wageBase) * suta.rate;
  return { socialSecurity, medicare, futa, suta: sutaTax, total: socialSecurity + medicare + futa + sutaTax };
}

/** A stored year of the admin tax table (EC8). Nothing counts as confirmed until an admin says so. */
export interface TaxTableDoc extends PayrollTaxYear {
  confirmed: boolean;
  confirmedBy?: string;
  confirmedAt?: string;
  note?: string;
}

/**
 * The seed for a year: 2026 federal figures (Social Security wage base $184,500 per SSA; Medicare
 * 1.45%; FUTA 0.6% after the full state credit, on $7,000). SUTA rates are employer-specific, so no
 * state is guessed. Other years start as a copy, flagged for the wage bases to be updated.
 */
export function placeholderTaxTable(year: number): TaxTableDoc {
  return {
    year,
    socialSecurity: { rate: 0.062, wageBase: 184_500 },
    medicare: { rate: 0.0145 },
    futa: { rate: 0.006, wageBase: 7_000 },
    suta: {},
    confirmed: false,
    note:
      year === 2026
        ? "Placeholder. Federal figures are the published 2026 values. Add each state's SUTA rate and wage base from its rate notice, and check for a FUTA credit reduction."
        : `Placeholder copied from the 2026 figures. Update the ${year} Social Security wage base, SUTA rates and any FUTA credit reduction.`,
  };
}

export function validateTaxTable(t: Partial<PayrollTaxYear>): string[] {
  const errors: string[] = [];
  const rate = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v < 0.2;
  const base = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v < 10_000_000;
  if (!Number.isInteger(t.year)) errors.push("The table needs a year.");
  if (!rate(t.socialSecurity?.rate) || !base(t.socialSecurity?.wageBase)) errors.push("Social Security needs a rate under 20% and a wage base.");
  if (!rate(t.medicare?.rate)) errors.push("Medicare needs a rate under 20%.");
  if (!rate(t.futa?.rate) || !base(t.futa?.wageBase)) errors.push("FUTA needs a rate under 20% and a wage base.");
  for (const [state, s] of Object.entries(t.suta ?? {})) {
    if (!/^[A-Z]{2}$/.test(state)) errors.push(`"${state}" isn't a two-letter state code.`);
    if (!rate(s?.rate) || !base(s?.wageBase)) errors.push(`${state} SUTA needs a rate under 20% and a wage base.`);
  }
  return errors;
}

/** Taxes as a share of wages: what planning uses for hourly staff, so a rate is stable all year. */
export function effectiveTaxRate(annualWages: number, table: PayrollTaxYear, state: string): number {
  return annualWages > 0 ? employerTaxes(annualWages, table, state).total / annualWages : 0;
}
